/**
 * MODULE: assistantQuotaGuidance.ts
 * PURPOSE: Proactive UX when workspace AI token quota blocks assistant streams (HTTP 429) + shell renew banner copy.
 * PUBLIC_API: mark/clear/read quota session flag, settings paths, composer + shell renew banner copy
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-08-06 [2.2.2133] — Shell renew banner copy + session dismiss for AppLayout top banner.
 * RECENT_PRIOR: 2026-06-12 [2.2.1367] — User-facing quota exceeded copy + HTTP 429 body parsing; **`ASSISTANT_QUOTA_BILLING_PATH`**.
 * FILE_SIZE: ~160 / 200 lines
 */

import {
  formatAssistantQuotaExceededMessage,
  isAssistantQuotaExceededError,
} from '../../hooks/assistant/streamOrchestrationAssistant';

export const ASSISTANT_QUOTA_SETTINGS_PATH = '/settings/ai-usage';

export const ASSISTANT_QUOTA_BILLING_PATH = '/settings/billing';

export const ASSISTANT_QUOTA_SESSION_KEY = 'brazn_assistant_quota_exceeded';

export function markAssistantQuotaExceeded(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(ASSISTANT_QUOTA_SESSION_KEY, String(Date.now()));
  } catch {
    /* private mode */
  }
}

export function clearAssistantQuotaExceeded(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(ASSISTANT_QUOTA_SESSION_KEY);
  } catch {
    /* private mode */
  }
}

export function readAssistantQuotaExceeded(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(ASSISTANT_QUOTA_SESSION_KEY) != null;
  } catch {
    return false;
  }
}

/** Short copy for assistant error bubbles (no developer env hints). */
export function assistantQuotaExceededBubbleMessage(): string {
  return (
    'Your AI usage quota has been exceeded for this workspace. ' +
    'Open AI usage in Settings to review your limit and top up your plan.'
  );
}

export type AssistantQuotaSeverity = 'warning' | 'critical' | 'exceeded';

export const ASSISTANT_QUOTA_WARNING_DISMISS_PREFIX = 'brazn_assistant_quota_warn_dismiss_';

export function assistantQuotaWarningDismissKey(threshold: number): string {
  return `${ASSISTANT_QUOTA_WARNING_DISMISS_PREFIX}${threshold}`;
}

export function dismissAssistantQuotaWarning(threshold: number): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(assistantQuotaWarningDismissKey(threshold), String(Date.now()));
  } catch {
    /* private mode */
  }
}

export function isAssistantQuotaWarningDismissed(threshold: number): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(assistantQuotaWarningDismissKey(threshold)) != null;
  } catch {
    return false;
  }
}

export function assistantQuotaBannerCopyForSeverity(
  severity: AssistantQuotaSeverity,
  percentUsed?: number,
): string {
  const pct = percentUsed != null ? `${Math.round(percentUsed)}%` : '';
  if (severity === 'exceeded') {
    return assistantQuotaExceededBubbleMessage();
  }
  if (severity === 'critical') {
    return (
      `Your workspace has used ${pct || 'over 90%'} of its AI token allowance. ` +
      'Top up credits soon to avoid interrupted assistant chat.'
    );
  }
  return (
    `Your workspace has used ${pct || 'over 75%'} of its AI token allowance. ` +
    'Consider buying AI credits or enabling auto top-up in Settings.'
  );
}

/** Session dismiss for AppLayout “time to renew” banner when tokens are exhausted. */
export const SHELL_RENEW_EXCEEDED_DISMISS_KEY = 'brazn_shell_renew_exceeded_dismiss';

export function dismissShellRenewExceeded(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(SHELL_RENEW_EXCEEDED_DISMISS_KEY, String(Date.now()));
  } catch {
    /* private mode */
  }
}

export function isShellRenewExceededDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(SHELL_RENEW_EXCEEDED_DISMISS_KEY) != null;
  } catch {
    return false;
  }
}

export function clearShellRenewExceededDismiss(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(SHELL_RENEW_EXCEEDED_DISMISS_KEY);
  } catch {
    /* private mode */
  }
}

/** App-shell top banner copy (renew / running low) — distinct from assistant composer copy. */
export function shellRenewBannerCopyForSeverity(
  severity: AssistantQuotaSeverity,
  percentUsed?: number,
): string {
  const pct = percentUsed != null ? `${Math.round(percentUsed)}%` : '';
  if (severity === 'exceeded') {
    return (
      'Time to renew — your AI tokens are used up. ' +
      'Top up credits or manage your plan to keep using the assistant.'
    );
  }
  if (severity === 'critical') {
    return (
      `Your AI tokens are running low (${pct || 'over 90%'} used). ` +
      'Top up soon to avoid interruption.'
    );
  }
  return (
    `Your workspace has used ${pct || 'over 75%'} of its AI token allowance. ` +
    'Consider topping up credits in Settings.'
  );
}

/** @deprecated use assistantQuotaBannerCopyForSeverity('exceeded') */
export function assistantQuotaBannerCopy(): string {
  return assistantQuotaExceededBubbleMessage();
}

/** Map platform/orchestration 429 JSON into user-facing assistant error text. */
export function quotaExceededMessageFromHttpBody(body: Record<string, unknown>): string {
  const error = String(body.error ?? '').trim();
  const detail = String(body.detail ?? '').trim();
  if (
    isAssistantQuotaExceededError(error) ||
    isAssistantQuotaExceededError(detail) ||
    (detail.toLowerCase().includes('current usage') && detail.toLowerCase().includes('limit'))
  ) {
    return formatAssistantQuotaExceededMessage(body);
  }
  if (error || detail) {
    return formatAssistantQuotaExceededMessage({ detail: detail || error });
  }
  return assistantQuotaExceededBubbleMessage();
}

export function quotaExceededMessageFromHttpStatus(
  status: number,
  body: Record<string, unknown>,
): string | null {
  if (status !== 429) return null;
  return quotaExceededMessageFromHttpBody(body);
}

export { isAssistantQuotaExceededError };
